import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useHistory } from "react-router-dom";
import { AxiosError } from "axios";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import useBasePath from "src/utils/hooks/useBasePath";

import {
  IAbsenceGroup,
  ICustomErrorDetails,
  INewAbsenceGroup,
  IAbsenceGroupsOrderConfiguration,
} from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroups.models";
import { IFormValues } from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroupsEdit";
import {
  changeStatusAbsenceGroup,
  createAbsenceGroup,
  editAbsenceGroup,
  fetchAbsenceGroup,
  fetchAbsenceGroups,
  fetchAbsenceGroupsOrderConfiguration,
  saveAbsenceGroupsOrderConfiguration,
} from "./api";
import { AbsenceQueryKeys } from "./config";

interface ISaveAbsenceGroupMutation {
  companyId: string;
  groupId: string;
  isNew: boolean;
}

interface IAbsenceGroupStatusArgs {
  groupId: string;
  groupName: string;
  active: boolean;
  eTag: string;
}

const AbsenceGroupsQueries = {
  list: (companyId: string) =>
    queryOptions<IAbsenceGroup[], Error>({
      queryKey: AbsenceQueryKeys.absenceGroups(companyId),
      queryFn: ({ signal }) => fetchAbsenceGroups({ companyId, signal }),
      initialData: [],
    }),

  details: (groupId: string, enabled: boolean = true) =>
    queryOptions<IAbsenceGroup, Error>({
      enabled,
      queryKey: AbsenceQueryKeys.absenceGroup(groupId),
      queryFn: ({ signal }) => fetchAbsenceGroup({ groupId, signal }),
    }),

  changeStatus: mutationOptions<
    unknown,
    AxiosError<ICustomErrorDetails>,
    IAbsenceGroupStatusArgs
  >({
    mutationFn: ({ groupId, active, eTag }) =>
      changeStatusAbsenceGroup(groupId, active, eTag),
  }),

  orderConfiguration: (companyId: string) =>
    queryOptions<IAbsenceGroupsOrderConfiguration, Error>({
      queryKey: AbsenceQueryKeys.absenceGroupsOrderConfiguration(companyId),
      queryFn: ({ signal }) =>
        fetchAbsenceGroupsOrderConfiguration(companyId, signal),
    }),

  saveOrderConfiguration: (companyId: string) =>
    mutationOptions<
      IAbsenceGroupsOrderConfiguration,
      AxiosError,
      IAbsenceGroupsOrderConfiguration
    >({
      mutationFn: (formData) =>
        saveAbsenceGroupsOrderConfiguration(companyId, formData),
    }),
};

export const useChangeStatusAbsenceGroup = () =>
  useMutation<
    unknown,
    AxiosError<ICustomErrorDetails>,
    IAbsenceGroupStatusArgs
  >(AbsenceGroupsQueries.changeStatus);

export const useFetchAbsenceGroups = (companyId: string) =>
  useQuery(AbsenceGroupsQueries.list(companyId));

export const useFetchAbsenceGroup = (
  groupId: string,
  enabled: boolean = true
) => useQuery(AbsenceGroupsQueries.details(groupId, enabled));

export const useFetchAbsenceGroupsOrderConfiguration = (companyId: string) =>
  useQuery(AbsenceGroupsQueries.orderConfiguration(companyId));

export const useSaveAbsenceGroupsOrderConfiguration = (
  companyId: string,
  onSuccess?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...AbsenceGroupsQueries.saveOrderConfiguration(companyId),
    onSuccess: () => {
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: AbsenceQueryKeys.absenceGroups(companyId),
        }),
        queryClient.invalidateQueries({
          queryKey: AbsenceQueryKeys.absenceTypesAll(),
        }),
      ]);
      dynamicNotification(
        __("Pomyślnie zapisano kolejność grup nieobecności.")
      );
      onSuccess?.();
    },
    onError: () => {
      dynamicNotification(
        __("Nie udało się zapisać kolejności grup / szablonów nieobecności."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export const useSaveAbsenceGroup = ({
  companyId,
  groupId,
  isNew,
}: ISaveAbsenceGroupMutation) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const basePath = useBasePath();

  return useMutation({
    mutationFn: async (formData: IFormValues) => {
      const parsedFormData: INewAbsenceGroup = {
        ...formData,
        ownerId: companyId,
        active: isNew || !!formData.active,
      };

      if (!isNew) {
        parsedFormData.id = groupId;
      }

      return isNew
        ? createAbsenceGroup(parsedFormData)
        : editAbsenceGroup(groupId, parsedFormData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.absenceGroup(groupId),
      });
      dynamicNotification(__("Pomyślnie zapisano grupę nieobecności."));
      setTimeout(() => {
        history.push(`${basePath}/absences/absence-groups`);
      }, 500);
    },
    onError: () => {
      dynamicNotification(
        __("Nie udało się zapisać danych."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export const useValidateAbsenceGroupName = () => {
  const queryClient = useQueryClient();

  const validateGroupName = async (
    companyId: string,
    groupName: string,
    excludeGroupId?: string
  ): Promise<boolean> => {
    const groupsData: IAbsenceGroup[] = await queryClient.fetchQuery({
      queryKey: AbsenceQueryKeys.absenceGroups(companyId),
      queryFn: async ({ signal }) =>
        fetchAbsenceGroups({ companyId, name: groupName, signal }),
    });

    return groupsData.some((group) => group.id !== excludeGroupId);
  };

  return { validateGroupName };
};

export default AbsenceGroupsQueries;
